import { useEffect, useRef } from 'react';
import { TOOLTIP_DELAY_MS } from './useEventTooltip';

function useEventDidMount({
  handleEventContextMenuOpen,
  handleEventDoubleClick,
  tooltipEnabled = false,
  handleShowEventTooltip,
  handleHideEventTooltip,
}) {
  const eventListenersRef = useRef(new WeakMap());
  const tooltipEnabledRef = useRef(tooltipEnabled);

  // `tooltipEnabled` is read from a ref inside the DOM listeners below so
  // toggling it (e.g. from the settings panel) takes effect immediately,
  // without needing FullCalendar to remount the event elements.
  useEffect(() => {
    tooltipEnabledRef.current = tooltipEnabled;

    if (!tooltipEnabled) {
      handleHideEventTooltip();
    }
  }, [tooltipEnabled, handleHideEventTooltip]);

  const handleEventDidMount = (info) => {
    const { el: element } = info;
    /**
     * Attach native DOM event listeners to the rendered event element.
     *
     * This is useful for interactions that FullCalendar doesn't expose as
     * callbacks (e.g. `contextmenu` for right-click support).
     *
     * See the MDN Event Reference for all supported DOM events:
     * https://developer.mozilla.org/en-US/docs/Web/API/Element#events
     */
    // element.addEventListener('focus', () => {
    //   registerFocusedEvent(info.el);
    // });

    const handleContextMenu = (e) => {
      handleEventContextMenuOpen(info.event, e);
    };

    const handleDoubleClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      handleEventDoubleClick(info.event, e);
    };

    element.addEventListener('contextmenu', handleContextMenu);
    element.addEventListener('dblclick', handleDoubleClick);

    let tooltipTimer = null;
    let tooltipShown = false;

    const clearTooltipTimer = () => {
      clearTimeout(tooltipTimer);
      tooltipTimer = null;
    };

    // (Re)schedules the tooltip to appear at `mousePos` after the hover
    // delay. Called on both mouseenter and mousemove so moving the cursor
    // resets the wait instead of showing the tooltip mid-move.
    const scheduleTooltip = (mousePos) => {
      clearTooltipTimer();
      tooltipTimer = setTimeout(() => {
        handleShowEventTooltip(info.event, mousePos);
        tooltipShown = true;
      }, TOOLTIP_DELAY_MS);
    };

    const handleMouseEnter = (e) => {
      if (!tooltipEnabledRef.current) return;

      tooltipShown = false;
      scheduleTooltip({ x: e.clientX, y: e.clientY });
    };

    const handleMouseMove = (e) => {
      if (!tooltipEnabledRef.current) return;

      if (tooltipShown) {
        handleHideEventTooltip();
        tooltipShown = false;
      }

      scheduleTooltip({ x: e.clientX, y: e.clientY });
    };

    const handleMouseLeave = () => {
      clearTooltipTimer();
      tooltipShown = false;
      handleHideEventTooltip();
    };

    element.addEventListener('mouseenter', handleMouseEnter);
    element.addEventListener('mousemove', handleMouseMove);
    element.addEventListener('mouseleave', handleMouseLeave);

    eventListenersRef.current.set(element, {
      handleContextMenu,
      handleDoubleClick,
      handleMouseEnter,
      handleMouseMove,
      handleMouseLeave,
      clearTooltipTimer,
    });
  };

  // Clean up listeners when the event is unmounted
  const handleEventWillUnmount = (info) => {
    const listeners = eventListenersRef.current.get(info.el);
    if (!listeners) return;

    info.el.removeEventListener('contextmenu', listeners.handleContextMenu);
    info.el.removeEventListener('dblclick', listeners.handleDoubleClick);
    info.el.removeEventListener('mouseenter', listeners.handleMouseEnter);
    info.el.removeEventListener('mousemove', listeners.handleMouseMove);
    info.el.removeEventListener('mouseleave', listeners.handleMouseLeave);
    listeners.clearTooltipTimer();

    eventListenersRef.current.delete(info.el);
  };

  return {
    handleEventDidMount,
    handleEventWillUnmount,
  };
}

export default useEventDidMount;
